import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { WizardProse, WizardButtons, WizardHeading } from '@/components/WizardParts'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig } from '@/layers/restore-wizard/types'

type SnapshotRef = components['schemas']['SnapshotRef']
type RefType = components['schemas']['RefType']
type ProbePresence = components['schemas']['ProbePresence']

const PRESENCE_LABEL: Record<string, string> = { PRESENT: 'Present at destination', ABSENT: 'Absent at destination', NOT_CHECKED: 'Not checked' }
const REF_TYPE_FILTERS: readonly { label: string; value: RefType | undefined }[] = [
  { label: 'All', value: undefined },
  { label: 'Branches', value: 'BRANCH' },
  { label: 'Tags', value: 'TAG' },
]

/**
 * `ref-wz-2` — the extra `select` pane the single-ref machine needs (`_family-wizard.md`): pick
 * the ref itself, from `jobListSnapshotRefs` (the snapshot's own tip list — never
 * `repo_ref_state`, §7a's live-state fence, `OI-A13`). `probeRefPresence` labels each visible ref
 * with **advisory** presence at the destination, bounded by the selection rather than a census
 * (§18.4a rule 1) — never rendered as the outcome, always with `checked_at`.
 *
 * **Real cursor pagination, a real `ref_type` filter, and a `MachineTime` fix all added W162.**
 * `jobListSnapshotRefs` is cursor-paginated on the server and previously had no `cursor` wired at
 * all — the same silent-truncation gap `SelectRestorePointPane` had. Its own `ref_type` query
 * parameter is real and server-documented ("the operator filters on the screen"), wired here as the
 * spec's own `segmented-control` (All/Branches/Tags) rather than invented. `tip_committed_at` is a
 * `MachineTime` with no served `ScreenTime` twin on `SnapshotRef` — it was rendered raw; now reads
 * through `formatJobTime()`, the established narrow fallback for exactly this class of field.
 *
 * **`grid-filter-row` added (W170).** The `ref_type` `segmented-control` built at
 * W162 was left inside `grid-tools` rather than its own row, an oversight from that pass rather
 * than a design choice — moved into a real `grid-filter-row`, matching the spec's own placement, no
 * new content. **`link` (in `alert`) is not built** — there is still no destination named anywhere
 * for it, in this pane's own spec or the shared family file; left undone rather than guessed at.
 */
export function SelectRefPane({
  config,
  heading,
  jobId,
  repositoryId,
  onNext,
  onBack,
}: {
  config: RestoreWizardConfig
  heading: string
  jobId: string
  repositoryId: string
  onNext: (ref: string) => void
  onBack: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const [ref, setRef] = useState('')
  const [refType, setRefType] = useState<RefType | undefined>(undefined)
  const refsPager = useCursorPager()

  const refs = useQuery({
    queryKey: ['snapshot-refs', orgId, workspaceId, jobId, repositoryId, refType, refsPager.cursor],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots/{job_id}/repositories/{repo_id}/refs', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, job_id: jobId, repo_id: repositoryId }, query: { ref_type: refType, cursor: refsPager.cursor } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })
  const refRows = (refs.data?.items ?? []) as unknown as readonly SnapshotRef[]

  const presence = useQuery({
    queryKey: ['ref-presence', orgId, workspaceId, repositoryId, ref],
    enabled: Boolean(ref),
    queryFn: async (): Promise<ProbePresence> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/probes/ref', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { repository_id: repositoryId, ref: [ref] } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as ProbePresence
    },
  })

  const problem = refs.error instanceof ApiProblemError ? refs.error.problem : undefined
  const item = presence.data?.items.find((i) => i.key === ref)

  return (
    <div data-scr={brandUnitId(config.unitIds.selectRef!)} data-cmp="pane">
      {/* `ref-wz-2`'s own shape is `wizsplit onecol` — a single, full-width column, unlike every
       * other `select`/`targets`-role pane's 1fr/420px split: there is no aside card here at all,
       * matching this pane's own lack of a destination/scope field (that comes later, on `ref-wz-3`). */}
      <WizardHeading>{heading}</WizardHeading>
      <WizardProse>Pick the ref this restore recovers. A single-ref restore writes exactly one ref and touches nothing else.</WizardProse>
      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mb-7">
        <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
          <span className="flex-1">Refs</span>
          <div data-cmp="grid-tools" className="flex items-center gap-4">
            <IconButton
              data-act={undefined}
              icon="i-refresh"
              label="Refresh"
              disabled={refs.isFetching}
              onClick={() => queryClient.invalidateQueries({ queryKey: ['snapshot-refs', orgId, workspaceId, jobId, repositoryId] })}
            />
          </div>
        </div>
        <div data-cmp="grid-filter-row" className="px-5 py-3 border-b border-bd">
          <div data-cmp="segmented-control" className="flex gap-2">
            {REF_TYPE_FILTERS.map((f) => (
              <button
                key={f.label}
                type="button"
                data-cmp="segment-option"
                onClick={() => {
                  refsPager.reset()
                  setRefType(f.value)
                }}
                className={`py-2 px-4 rounded-1 text-small font-semibold ${refType === f.value ? 'bg-blue text-white' : 'bg-row text-tx2'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <GridTable
          columns={[
            { key: 'pick', header: '', render: (r) => <input type="radio" name="ref" checked={ref === r.ref_name} onChange={() => setRef(r.ref_name)} /> },
            { key: 'ref', header: 'Ref', render: (r) => <span className="font-mono">{r.ref_name}</span> },
            { key: 'type', header: 'Type', render: (r) => <Chip variant="plain">{r.ref_type === 'TAG' ? 'Tag' : 'Branch'}</Chip> },
            { key: 'tip', header: 'Tip', render: (r) => <span className="font-mono">{r.tip_sha.slice(0, 12)}</span> },
            { key: 'committed', header: 'Committed', render: (r) => formatJobTime(r.tip_committed_at) },
            { key: 'author', header: 'Author', render: (r) => r.tip_author },
          ]}
          rows={refRows}
          getRowKey={(r) => r.ref_name}
          emptyMessage={refs.isLoading ? '' : 'Nothing in this snapshot matches.'}
        />
        <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
          <Note>Paged by cursor only — no total is served for this list.</Note>
          <Pager
            hasPrev={refsPager.hasPrev}
            hasNext={Boolean(refs.data?.next_cursor)}
            loading={refs.isFetching}
            onPrev={refsPager.goPrev}
            onNext={() => {
              if (refs.data?.next_cursor) refsPager.goNext(refs.data.next_cursor)
            }}
          />
        </div>
      </div>

      {ref && (
        <Note>
          {item ? `${PRESENCE_LABEL[item.presence] ?? item.presence}, checked ${presence.data?.checked_at}` : 'Checking presence…'}
          {presence.data?.advisory_note ? ` — ${presence.data.advisory_note}` : ' — each object is re-read as it is written; the report is what is true.'}
        </Note>
      )}

      <WizardButtons>
        <Button data-act={actionId(`nav.${config.unitIds.select}`)} ghost onClick={onBack}>
          Back
        </Button>
        <Button data-act={actionId(`nav.${config.unitIds.targets}`)} disabled={!ref} onClick={() => onNext(ref)}>
          Next
        </Button>
      </WizardButtons>
    </div>
  )
}
